import { FormerieRuntimeClient } from "./client.js";
import { messageForErrorCode } from "./messages.js";
import {
  applyFormerieValidationErrors,
  clearFormerieValidationErrors,
  collectFormerieFormFields,
  createFormerieSubmitPayload,
  evaluatePublicFieldVisibility,
  getPublicSchemaField,
  listPublicSchemaFields,
  resolveFormerieFieldValue,
  type CreateFormerieSubmitPayloadOptions,
  type PublicSchemaFieldEntry,
  type PublicSchemaInput,
} from "./runtime.js";
import {
  submitFormerieTarget,
  schemaFormerieTarget,
  type FormerieTarget,
} from "./targets.js";
import type {
  PublicArrayField,
  PublicFieldVisibilityState,
  PublicFormSchemaResponse,
  PublicFormSchemaResult,
  PublicFormStep,
  PublicScalarField,
  PublicSchemaNode,
  SubmitPayload,
  SubmitRequestOptions,
  SubmitResponse,
} from "./types.js";

export type FormerieRenderControl =
  | "array"
  | "checkbox"
  | "checkbox-group"
  | "input"
  | "select"
  | "textarea";

export interface FormerieRenderField {
  control: FormerieRenderControl;
  description?: string;
  id: string;
  inputMode?: string;
  inputType?: string;
  label: string;
  max?: number | string;
  maxLength?: number;
  min?: number | string;
  minLength?: number;
  multiple?: boolean;
  name: string;
  node: PublicArrayField | PublicScalarField;
  options?: string[];
  placeholder?: string;
  path: string;
  required: boolean;
  step?: string;
  value?: unknown;
  visibility: PublicFieldVisibilityState;
}

export interface FormerieRenderStep {
  fields: FormerieRenderField[];
  id: string;
  label: string;
  validateOn?: PublicFormStep["validateOn"];
}

export interface FormerieRenderModel {
  fields: FormerieRenderField[];
  fieldsByPath: Record<string, FormerieRenderField>;
  steps: FormerieRenderStep[];
  unsteppedFields: FormerieRenderField[];
}

export interface DescribeFormerieRenderFieldsOptions {
  includeHidden?: boolean;
}

export interface DescribeFormerieRenderModelOptions extends DescribeFormerieRenderFieldsOptions {
  includeEmptySteps?: boolean;
}

export type FormerieHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface FormerieRenderClassNames {
  checkboxField?: string;
  field?: string;
  fieldDescription?: string;
  fieldError?: string;
  honeypot?: string;
  status?: string;
  verification?: string;
  verificationError?: string;
}

export interface RenderFormerieFormHtmlOptions {
  classes?: FormerieRenderClassNames;
  formAttributes?: Record<string, boolean | number | string | null | undefined>;
  headingLevel?: FormerieHeadingLevel;
  honeypotField?: string;
  honeypotLabel?: string;
  includeHeading?: boolean;
  includeHiddenFields?: boolean;
  includeVerification?: boolean;
  submitLabel?: string;
  values?: Record<string, unknown>;
}

export interface RenderFormerieVerificationHtmlOptions {
  containerClass?: string;
  errorClass?: string;
}

export interface FormerieVerificationHtml {
  attributes: string;
  markup: string;
  scripts: string;
}

export interface FormerieVerificationTokenOptions {
  form: HTMLFormElement;
}

export type FormerieRuntimeMountTarget = FormerieTarget | {
  formBound: true;
};

export interface MountFormerieFormOptions extends RenderFormerieFormHtmlOptions {
  client?: FormerieRuntimeClient;
  endpoint?: string;
  fetcher?: typeof fetch;
  focusInvalid?: boolean;
  loadVerificationScript?: boolean;
  metadata?: CreateFormerieSubmitPayloadOptions["metadata"] | (() => CreateFormerieSubmitPayloadOptions["metadata"] | Promise<CreateFormerieSubmitPayloadOptions["metadata"]>);
  onError?: (response: SubmitResponse) => void;
  onSubmit?: (payload: SubmitPayload) => SubmitResponse | Promise<SubmitResponse>;
  onSuccess?: (response: SubmitResponse & { ok: true }) => void;
  pendingMessage?: string;
  resetOnSuccess?: boolean;
  schema: PublicFormSchemaResponse;
  submitOptions?: SubmitRequestOptions;
  submitUrl?: string;
  target?: FormerieRuntimeMountTarget;
  turnstileToken?: string | (() => string | Promise<string | undefined> | undefined);
  verificationToken?: string | (() => string | Promise<string | undefined> | undefined);
}

export type BindFormerieFormOptions = Omit<MountFormerieFormOptions, "formAttributes" | "includeHiddenFields" | "submitLabel">;

export interface ReadFormerieHostedSchemaOptions {
  root?: ParentNode;
  schemaElement?: HTMLScriptElement | string;
}

export interface BindFormerieHostedFormOptions extends Omit<BindFormerieFormOptions, "schema"> {
  form?: HTMLFormElement | string;
  root?: ParentNode;
  schema?: PublicFormSchemaResponse;
  schemaElement?: HTMLScriptElement | string;
}

export interface LoadFormerieRuntimeSchemaOptions {
  client?: FormerieRuntimeClient;
  endpoint?: string;
  schemaOptions?: SubmitRequestOptions;
  target: FormerieRuntimeMountTarget;
}

export interface MountFormerieRuntimeFormOptions
  extends Omit<MountFormerieFormOptions, "client" | "endpoint" | "schema" | "target">,
    LoadFormerieRuntimeSchemaOptions {}

export interface MountedFormerieForm {
  destroy(): void;
  element: Element;
  form: HTMLFormElement;
  submit(): Promise<SubmitResponse>;
  values(): Record<string, unknown>;
}

const DEFAULT_HOSTED_FORM_SELECTOR = "form[data-formerie-form]";
const DEFAULT_HOSTED_SCHEMA_SELECTOR = 'script[type="application/json"][data-formerie-schema]';

interface RenderFormerieFormContext {
  classes: FormerieRenderClassNames;
}

export async function loadFormerieRuntimeSchema(
  options: LoadFormerieRuntimeSchemaOptions,
): Promise<PublicFormSchemaResult> {
  const client = options.client ?? new FormerieRuntimeClient({ endpoint: options.endpoint });

  if ("formBound" in options.target) {
    return client.schemaForm(options.schemaOptions);
  }

  return schemaFormerieTarget(client, options.target, options.schemaOptions);
}

export async function mountFormerieRuntimeForm(
  target: Element | string,
  options: MountFormerieRuntimeFormOptions,
): Promise<MountedFormerieForm> {
  const schema = await loadFormerieRuntimeSchema(options);
  if (!schema.ok) {
    throw new Error(schema.error.message);
  }

  const client = options.client ?? new FormerieRuntimeClient({ endpoint: options.endpoint });
  const { schemaOptions: _schemaOptions, ...mountOptions } = options;

  return mountFormerieForm(target, {
    ...mountOptions,
    client,
    schema,
    target: options.target,
  });
}

export function describeFormerieRenderFields(
  schema: PublicSchemaInput,
  values: Record<string, unknown> = {},
  options: DescribeFormerieRenderFieldsOptions = {},
): FormerieRenderField[] {
  const fields: FormerieRenderField[] = [];
  const visibility = evaluatePublicFieldVisibility(schema, values);

  for (const entry of listPublicSchemaFields(schema)) {
    const state = visibility[entry.path] ?? "visible";
    if (state === "hidden" && options.includeHidden !== true) {
      continue;
    }

    const field = renderFieldForEntry(entry, values, state);
    if (field) {
      fields.push(field);
    }
  }

  return fields;
}

export function describeFormerieRenderModel(
  schema: PublicSchemaInput,
  values: Record<string, unknown> = {},
  options: DescribeFormerieRenderModelOptions = {},
): FormerieRenderModel {
  const fields = describeFormerieRenderFields(schema, values, options);
  const fieldsByPath = Object.fromEntries(fields.map((field) => [field.path, field]));
  const steppedPaths = new Set<string>();
  const steps = isPublicFormSchema(schema)
    ? renderStepsForFields(schema.steps ?? [], fields, steppedPaths, options)
    : [];

  return {
    fields,
    fieldsByPath,
    steps,
    unsteppedFields: fields.filter((field) => !steppedPaths.has(field.path)),
  };
}

export function renderFormerieFormHtml(
  schema: PublicSchemaInput,
  options: RenderFormerieFormHtmlOptions = {},
): string {
  const context = renderFormerieFormContext(options);
  const values = options.values ?? {};
  const includeHidden = options.includeHiddenFields ?? true;
  const visibility = evaluatePublicFieldVisibility(schema, values);
  const renderModel = describeFormerieRenderModel(schema, values, { includeHidden });
  const stepPaths = new Set(renderModel.steps.flatMap((step) => step.fields.map((field) => field.path)));
  const steps = isPublicFormSchema(schema)
    ? renderStepSectionsHtml(schema, values, visibility, includeHidden, context)
    : "";
  const fields = renderSchemaNodesHtml(schemaFields(schema), values, visibility, includeHidden, context, "", stepPaths);
  const honeypot = options.honeypotField
    ? renderHoneypotField(options.honeypotField, options.honeypotLabel, context)
    : "";
  const label = schemaTitle(schema);
  const description = schemaDescription(schema);
  const submitLabel = options.submitLabel ?? schemaSubmitLabel(schema) ?? "Submit";
  const verification = options.includeVerification === false || !isPublicFormSchema(schema)
    ? emptyVerificationHtml()
    : renderFormerieVerificationHtml(schema.verification, {
      containerClass: context.classes.verification,
      errorClass: context.classes.verificationError ?? context.classes.fieldError,
    });
  const attributes = serializeAttributes({
    "data-formerie-form": "",
    novalidate: true,
    ...options.formAttributes,
  }) + verification.attributes;
  const heading = options.includeHeading === false
    ? ""
    : renderFormHeading(label, description, options.headingLevel);

  return `<form${attributes}>` +
    heading +
    `<div data-formerie-fields>${honeypot}${steps}${fields}</div>` +
    verification.markup +
    `<div${serializeAttributes({
      "class": context.classes.status,
      "data-formerie-status": "",
      role: "status",
      "aria-live": "polite",
    })}></div>` +
    `<button type="submit" data-formerie-submit>${escapeHtml(submitLabel)}</button>` +
    `</form>`;
}

export function renderFormerieVerificationHtml(
  verification: PublicFormSchemaResponse["verification"],
  options: RenderFormerieVerificationHtmlOptions = {},
): FormerieVerificationHtml {
  if (!verification?.required || !verification.siteKey) {
    return emptyVerificationHtml();
  }

  const attributes = serializeAttributes({
    "data-formerie-verification-required": "true",
    "data-formerie-verification-provider": verification.provider,
    "data-formerie-verification-site-key": verification.siteKey,
    "data-formerie-verification-action": verification.expectedAction,
  });
  const containerAttributes = serializeAttributes({
    "class": options.containerClass,
    "data-formerie-verification": "",
  });
  const error = `<span${serializeAttributes({
    "class": options.errorClass,
    "data-formerie-verification-error": "",
    hidden: true,
  })}></span>`;

  if (verification.provider === "turnstile") {
    return {
      attributes,
      markup: `<div${containerAttributes}><div class="cf-turnstile" data-sitekey="${escapeAttribute(verification.siteKey)}"></div>${error}</div>`,
      scripts: `<script src="${escapeAttribute(verificationScriptUrl(verification) ?? "")}" async defer></script>`,
    };
  }

  if (verification.provider === "recaptcha-v2") {
    return {
      attributes,
      markup: `<div${containerAttributes}><div class="g-recaptcha" data-sitekey="${escapeAttribute(verification.siteKey)}"></div>${error}</div>`,
      scripts: `<script src="${escapeAttribute(verificationScriptUrl(verification) ?? "")}" async defer></script>`,
    };
  }

  return {
    attributes,
    markup: `<div${containerAttributes}>${error}</div>`,
    scripts: `<script src="${escapeAttribute(verificationScriptUrl(verification) ?? "")}" async defer></script>`,
  };
}

function emptyVerificationHtml(): FormerieVerificationHtml {
  return { attributes: "", markup: "", scripts: "" };
}

function renderFormerieFormContext(options: RenderFormerieFormHtmlOptions): RenderFormerieFormContext {
  return {
    classes: options.classes ?? {},
  };
}

function renderFormHeading(
  label: string,
  description: string | undefined,
  level: FormerieHeadingLevel | undefined,
): string {
  const tag = headingTag(level);
  const descriptionMarkup = description
    ? `<p data-formerie-form-description>${escapeHtml(description)}</p>`
    : "";

  return `<div data-formerie-form-heading><${tag}>${escapeHtml(label)}</${tag}>${descriptionMarkup}</div>`;
}

function headingTag(level: FormerieHeadingLevel | undefined): `h${FormerieHeadingLevel}` {
  return `h${level ?? 2}` as `h${FormerieHeadingLevel}`;
}

function renderHoneypotField(
  path: string,
  label: string | undefined,
  context: RenderFormerieFormContext,
): string {
  return `<div${serializeAttributes({
    "class": context.classes.honeypot,
    "data-formerie-honeypot": "",
    hidden: true,
    "aria-hidden": "true",
  })}><label>${escapeHtml(label ?? "Leave this field empty")}<input${serializeAttributes({
    "data-formerie-honeypot": "",
    "data-formerie-path": path,
    "data-formerie-type": "string",
    name: path,
    type: "text",
    tabindex: -1,
    autocomplete: "off",
  })}></label></div>`;
}

function renderFieldDescriptionHtml(
  description: string,
  tag: "p" | "span",
  context: RenderFormerieFormContext,
): string {
  return `<${tag}${serializeAttributes({
    "class": context.classes.fieldDescription,
    "data-formerie-field-description": "",
  })}>${escapeHtml(description)}</${tag}>`;
}

function joinClassNames(...values: Array<string | undefined>): string | undefined {
  const classes = values
    .flatMap((value) => value?.split(/\s+/u) ?? [])
    .map((value) => value.trim())
    .filter(Boolean);

  return classes.length > 0 ? Array.from(new Set(classes)).join(" ") : undefined;
}

function verificationScriptUrl(
  verification: PublicFormSchemaResponse["verification"],
): string | undefined {
  if (!verification?.required || !verification.siteKey) {
    return undefined;
  }

  if (verification.provider === "turnstile") {
    return "https://challenges.cloudflare.com/turnstile/v0/api.js";
  }

  if (verification.provider === "recaptcha-v2") {
    return "https://www.google.com/recaptcha/api.js";
  }

  return `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(verification.siteKey)}`;
}

function loadFormerieVerificationScript(
  documentRef: Document | undefined,
  verification: PublicFormSchemaResponse["verification"],
): void {
  const scriptUrl = verificationScriptUrl(verification);
  const head = documentRef?.head;
  if (!scriptUrl || !documentRef || !head) {
    return;
  }

  const scripts = typeof documentRef.querySelectorAll === "function"
    ? Array.from(documentRef.querySelectorAll<HTMLScriptElement>("script"))
    : [];
  if (scripts.some((script) => script.src === scriptUrl || script.getAttribute("src") === scriptUrl)) {
    return;
  }

  const script = documentRef.createElement("script");
  script.src = scriptUrl;
  script.async = true;
  script.defer = true;
  script.setAttribute("data-formerie-verification-script", verification?.provider ?? "verification");
  head.appendChild(script);
}

export function mountFormerieForm(
  target: Element | string,
  options: MountFormerieFormOptions,
): MountedFormerieForm {
  const element = resolveMountElement(target);
  element.innerHTML = renderFormerieFormHtml(options.schema, {
    ...options,
    includeHiddenFields: true,
  });

  const form = element.querySelector<HTMLFormElement>("form[data-formerie-form]");
  if (!form) {
    throw new Error("Formerie form could not be mounted.");
  }

  if (options.loadVerificationScript !== false) {
    loadFormerieVerificationScript(form.ownerDocument ?? element.ownerDocument, options.schema.verification);
  }

  const mounted = bindFormerieForm(form, options);

  return {
    ...mounted,
    destroy() {
      mounted.destroy();
      element.innerHTML = "";
    },
    element,
  };
}

export function readFormerieHostedSchema(
  options: ReadFormerieHostedSchemaOptions = {},
): PublicFormSchemaResponse {
  const element = resolveHostedSchemaElement(options.root, options.schemaElement);
  const text = element.textContent?.trim() ?? "";

  if (!text) {
    throw new Error("Formerie hosted schema script is empty.");
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Formerie hosted schema script contains invalid JSON.");
  }

  if (!isPublicFormSchemaResponse(parsed)) {
    throw new Error("Formerie hosted schema script does not contain a public form schema.");
  }

  return parsed;
}

export function bindFormerieHostedForm(
  options: BindFormerieHostedFormOptions = {},
): MountedFormerieForm {
  const form = resolveHostedFormElement(options.root, options.form);
  const schemaRoot = options.root ?? form.ownerDocument ?? undefined;
  const schema = options.schema ?? readFormerieHostedSchema({
    root: schemaRoot,
    schemaElement: options.schemaElement,
  });
  const {
    form: _form,
    root: _root,
    schema: _schema,
    schemaElement: _schemaElement,
    ...bindOptions
  } = options;
  const verificationToken = bindOptions.verificationToken ??
    (bindOptions.turnstileToken ? undefined : () => collectFormerieVerificationToken({ form }));
  const metadata = bindOptions.metadata ?? (() => hostedFormMetadata());

  return bindFormerieForm(form, {
    ...bindOptions,
    metadata,
    resetOnSuccess: bindOptions.resetOnSuccess ?? true,
    schema,
    verificationToken,
  });
}

export function bindFormerieForm(
  form: HTMLFormElement,
  options: BindFormerieFormOptions,
): MountedFormerieForm {
  const bindOptions = defaultBindFormerieFormOptions(form, options);
  syncFormerieFormVisibility(form, bindOptions.schema, bindOptions.values ?? {});

  let submitting = false;
  const submit = async (): Promise<SubmitResponse> => {
    if (submitting) {
      return {
        ok: false,
        error: {
          code: "BAD_REQUEST",
          message: "The form is already submitting.",
        },
      };
    }

    submitting = true;
    setSubmitDisabled(form, true);
    try {
      return await submitMountedForm(form, bindOptions);
    } finally {
      submitting = false;
      setSubmitDisabled(form, false);
    }
  };
  const handleSubmit = (event: SubmitEvent): void => {
    event.preventDefault();
    void submit();
  };
  const handleChange = (): void => {
    syncFormerieFormVisibility(form, bindOptions.schema);
  };

  form.addEventListener("submit", handleSubmit);
  form.addEventListener("input", handleChange);
  form.addEventListener("change", handleChange);

  return {
    destroy() {
      form.removeEventListener("submit", handleSubmit);
      form.removeEventListener("input", handleChange);
      form.removeEventListener("change", handleChange);
    },
    element: form,
    form,
    submit,
    values() {
      return collectFormerieFormFields(form, { schema: bindOptions.schema });
    },
  };
}

function defaultBindFormerieFormOptions(
  form: HTMLFormElement,
  options: BindFormerieFormOptions,
): BindFormerieFormOptions {
  if (options.verificationToken !== undefined || options.turnstileToken !== undefined) {
    return options;
  }

  return {
    ...options,
    verificationToken: () => collectFormerieVerificationToken({ form }),
  };
}

export function syncFormerieFormVisibility(
  form: HTMLFormElement,
  schema: PublicSchemaInput,
  values: Record<string, unknown> = collectFormerieFormFields(form, { schema }),
): Record<string, PublicFieldVisibilityState> {
  const visibility = evaluatePublicFieldVisibility(schema, values);

  for (const container of Array.from(form.querySelectorAll<HTMLElement>("[data-formerie-field]"))) {
    const path = container.getAttribute("data-formerie-field");
    if (!path) {
      continue;
    }

    const state = visibility[path] ?? "visible";
    const hidden = state === "hidden";
    container.hidden = hidden;
    container.setAttribute("data-formerie-visibility", state);

    for (const control of Array.from(container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-formerie-path]"))) {
      control.disabled = hidden;
    }
  }

  return visibility;
}

export async function collectFormerieVerificationToken(
  options: FormerieVerificationTokenOptions,
): Promise<string | undefined> {
  const form = options.form;
  if (formDataAttribute(form, "verification-required", "data-verification-required") !== "true") {
    return undefined;
  }

  const provider = formDataAttribute(form, "verification-provider", "data-verification-provider");
  const siteKey = formDataAttribute(form, "verification-site-key", "data-verification-site-key") ?? "";

  if (provider === "recaptcha-v3") {
    const grecaptcha = browserVerificationRuntime().grecaptcha;
    if (!grecaptcha || typeof grecaptcha.ready !== "function" || typeof grecaptcha.execute !== "function") {
      throw verificationError("Verification is still loading. Try again in a moment.");
    }

    const action = formDataAttribute(form, "verification-action", "data-verification-action") ?? "form_submit";
    return await new Promise((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action })
          .then((token) => {
            if (token) {
              resolve(token);
            } else {
              reject(verificationError("Complete the verification challenge."));
            }
          })
          .catch(() => reject(verificationError("Complete the verification challenge.")));
      });
    });
  }

  const responseName = provider === "turnstile" ? "cf-turnstile-response" : "g-recaptcha-response";
  const responseField = form.querySelector<HTMLInputElement>(`[name="${responseName}"]`);
  const token = typeof responseField?.value === "string" ? responseField.value.trim() : "";
  if (!token) {
    throw verificationError("Complete the verification challenge.");
  }

  return token;
}

export function applyFormerieVerificationError(
  form: HTMLFormElement,
  message: string,
): void {
  const container = form.querySelector<HTMLElement>("[data-formerie-verification]");
  const error = form.querySelector<HTMLElement>("[data-formerie-verification-error]");
  if (container) {
    container.setAttribute("data-invalid", "true");
    container.setAttribute("data-formerie-invalid", "true");
  }
  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
}

export function clearFormerieVerificationError(form: HTMLFormElement): void {
  const container = form.querySelector<HTMLElement>("[data-formerie-verification]");
  const error = form.querySelector<HTMLElement>("[data-formerie-verification-error]");
  if (container) {
    container.removeAttribute("data-invalid");
    container.removeAttribute("data-formerie-invalid");
  }
  if (error) {
    error.textContent = "";
    error.hidden = true;
  }
}

export function resetFormerieVerificationWidget(form: HTMLFormElement): void {
  const provider = formDataAttribute(form, "verification-provider", "data-verification-provider");
  const runtime = browserVerificationRuntime();

  if (provider === "turnstile" && typeof runtime.turnstile?.reset === "function") {
    runtime.turnstile.reset();
  } else if (provider === "recaptcha-v2" && typeof runtime.grecaptcha?.reset === "function") {
    runtime.grecaptcha.reset();
  }
}

async function submitMountedForm(
  form: HTMLFormElement,
  options: BindFormerieFormOptions,
): Promise<SubmitResponse> {
  clearFormerieValidationErrors(form);
  clearFormerieVerificationError(form);
  syncFormerieFormVisibility(form, options.schema);
  setStatus(form, options.pendingMessage ?? "Sending...", "pending");

  const response = await submitWithErrorBoundary(form, options);

  if (response.ok) {
    if (options.resetOnSuccess === true) {
      form.reset();
      clearFormerieValidationErrors(form);
      syncFormerieFormVisibility(form, options.schema);
    }
    resetFormerieVerificationWidget(form);
    setStatus(form, formDataAttribute(form, "success-message", "data-success-message") ?? schemaSuccessMessage(options.schema), "success");
    options.onSuccess?.(response);
    return response;
  }

  if (response.error.code === "TURNSTILE_FAILED") {
    applyFormerieVerificationError(form, response.error.message);
    resetFormerieVerificationWidget(form);
    setStatus(form, "Review the highlighted verification challenge.", "error");
    options.onError?.(response);
    return response;
  }

  const invalidPaths = applyFormerieValidationErrors(form, response);
  setStatus(form, response.error.message, "error");
  if ((options.focusInvalid ?? true) && invalidPaths.length > 0) {
    focusFirstInvalidField(form);
  }
  options.onError?.(response);
  return response;
}

async function submitWithErrorBoundary(
  form: HTMLFormElement,
  options: BindFormerieFormOptions,
): Promise<SubmitResponse> {
  const submitUrl = resolveSubmitUrl(form, options);
  if (!options.target && !options.onSubmit && !submitUrl) {
    return {
      ok: false,
      error: {
        code: "BAD_REQUEST",
        message: "Formerie form binding requires target, submitUrl, or onSubmit before submitting.",
      },
    };
  }

  try {
    const payload = createFormerieSubmitPayload(form, {
      metadata: await resolveMaybeProvider(options.metadata),
      schema: options.schema,
      turnstileToken: await resolveMaybeProvider(options.turnstileToken),
      verificationToken: await resolveMaybeProvider(options.verificationToken),
    });

    if (options.onSubmit) {
      return await options.onSubmit(payload);
    }

    if (submitUrl) {
      return await submitWithUrl(submitUrl, payload, options);
    }

    return await submitWithRuntimeClient(payload, options);
  } catch (error) {
    if (isFormerieVerificationError(error)) {
      return {
        ok: false,
        error: {
          code: "TURNSTILE_FAILED",
          message: error.message,
        },
      };
    }

    return {
      ok: false,
      error: {
        code: "NETWORK_ERROR",
        message: "The form could not be submitted.",
      },
    };
  }
}

async function submitWithRuntimeClient(
  payload: SubmitPayload,
  options: BindFormerieFormOptions,
): Promise<SubmitResponse> {
  if (!options.target) {
    throw new Error("Formerie form binding requires target, submitUrl, or onSubmit before submitting.");
  }

  const client = options.client ?? new FormerieRuntimeClient({ endpoint: options.endpoint });
  if ("formBound" in options.target) {
    return client.submitForm(payload, options.submitOptions);
  }

  return submitFormerieTarget(client, options.target, payload, options.submitOptions);
}

async function submitWithUrl(
  url: string,
  payload: SubmitPayload,
  options: BindFormerieFormOptions,
): Promise<SubmitResponse> {
  const fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
  const response = await fetcher(url, {
    method: "POST",
    headers: submitHeaders(options.submitOptions),
    body: JSON.stringify(payload),
    signal: options.submitOptions?.signal,
  });
  const body = await response.json().catch(() => null) as unknown;

  return normalizeSubmitResponse(body);
}

function submitHeaders(options: SubmitRequestOptions | undefined): Headers {
  const headers = new Headers(options?.headers);
  headers.set("Accept", "application/json");
  headers.set("Content-Type", "application/json");
  return headers;
}

function normalizeSubmitResponse(value: unknown): SubmitResponse {
  if (isRecord(value) && value.ok === true && value.status === "accepted" && typeof value.submissionId === "string") {
    return {
      ok: true,
      status: "accepted",
      submissionId: value.submissionId,
    };
  }

  if (isRecord(value) && value.ok === false && isRecord(value.error) && typeof value.error.code === "string" && typeof value.error.message === "string") {
    if (value.error.code !== "VALIDATION_FAILED") {
      return value as unknown as SubmitResponse;
    }

    if (isRecord(value.error.fields) && Array.isArray(value.error.issues)) {
      return value as unknown as SubmitResponse;
    }
  }

  return {
    ok: false,
    error: {
      code: "INVALID_RESPONSE",
      message: messageForErrorCode("INVALID_RESPONSE"),
    },
  };
}

function resolveSubmitUrl(form: HTMLFormElement, options: BindFormerieFormOptions): string | undefined {
  return options.submitUrl ?? formDataAttribute(form, "submit-path", "data-submit-path") ?? undefined;
}

function formDataAttribute(form: HTMLFormElement, name: string, legacyName?: string): string | null {
  return form.getAttribute(`data-formerie-${name}`) ?? (legacyName ? form.getAttribute(legacyName) : null);
}

interface BrowserGrecaptcha {
  execute(siteKey: string, options: { action: string }): Promise<string | undefined>;
  ready(callback: () => void): void;
  reset?(): void;
}

interface BrowserTurnstile {
  reset?(): void;
}

function browserVerificationRuntime(): typeof globalThis & {
  grecaptcha?: BrowserGrecaptcha;
  turnstile?: BrowserTurnstile;
} {
  return globalThis as typeof globalThis & {
    grecaptcha?: BrowserGrecaptcha;
    turnstile?: BrowserTurnstile;
  };
}

class FormerieVerificationError extends Error {
  override name = "FormerieVerificationError";
}

function verificationError(message: string): FormerieVerificationError {
  return new FormerieVerificationError(message);
}

function isFormerieVerificationError(error: unknown): error is FormerieVerificationError {
  return error instanceof Error && error.name === "FormerieVerificationError";
}

function hostedFormMetadata(): Record<string, unknown> | undefined {
  if (typeof location === "undefined" || typeof location.href !== "string") {
    return undefined;
  }

  return {
    pageUrl: location.href,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function renderFieldForEntry(
  entry: PublicSchemaFieldEntry,
  values: Record<string, unknown>,
  visibility: PublicFieldVisibilityState,
): FormerieRenderField | null {
  if (entry.repeated || entry.node.type === "object") {
    return null;
  }

  return renderFieldForNode(entry.path, entry.node, values, visibility);
}

function renderFieldForNode(
  path: string,
  node: PublicSchemaNode,
  values: Record<string, unknown>,
  visibility: PublicFieldVisibilityState,
): FormerieRenderField | null {
  if (node.type === "object") {
    return null;
  }

  if (node.type === "array") {
    return {
      control: "array",
      description: node.description,
      id: fieldId(path),
      label: node.label ?? labelFromPath(path),
      name: path,
      node,
      path,
      required: node.required === true,
      value: resolvePublicFieldValue(values, path, undefined),
      visibility,
    };
  }

  return {
    control: controlForScalarField(node),
    description: node.description,
    id: fieldId(path),
    inputMode: inputModeForScalarField(node),
    inputType: inputTypeForScalarField(node),
    label: node.label ?? labelFromPath(path),
    ...controlConstraintsForScalarField(node),
    multiple: node.type === "checkbox" && Boolean(node.allowedValues?.length),
    name: node.type === "checkbox" && node.allowedValues?.length ? `${path}[]` : path,
    node,
    options: node.allowedValues,
    placeholder: node.placeholder,
    path,
    required: node.required === true,
    value: resolvePublicFieldValue(values, path, node.defaultValue),
    visibility,
  };
}

function renderSchemaNodesHtml(
  fields: Record<string, PublicSchemaNode>,
  values: Record<string, unknown>,
  visibility: Record<string, PublicFieldVisibilityState>,
  includeHidden: boolean,
  context: RenderFormerieFormContext,
  prefix = "",
  excludedPaths: Set<string> = new Set(),
): string {
  return Object.entries(fields)
    .map(([key, node]) => renderSchemaNodeHtml(
      joinPath(prefix, key),
      node,
      values,
      visibility,
      includeHidden,
      context,
      excludedPaths,
    ))
    .join("");
}

function renderSchemaNodeHtml(
  path: string,
  node: PublicSchemaNode,
  values: Record<string, unknown>,
  visibility: Record<string, PublicFieldVisibilityState>,
  includeHidden: boolean,
  context: RenderFormerieFormContext,
  excludedPaths: Set<string> = new Set(),
): string {
  if (excludedPaths.has(path)) {
    return "";
  }

  const state = visibility[path] ?? "visible";
  if (state === "hidden" && includeHidden !== true) {
    return "";
  }

  if (node.type === "object") {
    const childMarkup = renderSchemaNodesHtml(node.fields, values, visibility, includeHidden, context, path, excludedPaths);
    if (!childMarkup) {
      return "";
    }

    const description = node.description
      ? renderFieldDescriptionHtml(node.description, "p", context)
      : "";

    return `<fieldset${fieldContainerAttributes({ path, visibility: state }, context)}>` +
      `<legend>${escapeHtml(node.label ?? labelFromPath(path))}</legend>` +
      description +
      childMarkup +
      renderFieldError(path, context) +
      `</fieldset>`;
  }

  const field = renderFieldForNode(path, node, values, state);
  return field ? renderFieldHtml(field, context) : "";
}

function renderStepSectionsHtml(
  schema: PublicFormSchemaResponse,
  values: Record<string, unknown>,
  visibility: Record<string, PublicFieldVisibilityState>,
  includeHidden: boolean,
  context: RenderFormerieFormContext,
): string {
  if (!schema.steps || schema.steps.length === 0) {
    return "";
  }

  return schema.steps
    .map((step) => {
      const fields = step.fields
        .map((path) => renderSchemaPathHtml(schema, path, values, visibility, includeHidden, context))
        .join("");
      if (!fields) {
        return "";
      }

      const attributes = serializeAttributes({
        "data-formerie-step": step.id,
        "data-formerie-step-validate-on": step.validateOn,
      });

      return `<fieldset${attributes}>` +
        `<legend>${escapeHtml(step.label ?? labelFromPath(step.id))}</legend>` +
        fields +
        `</fieldset>`;
    })
    .join("");
}

function renderSchemaPathHtml(
  schema: PublicFormSchemaResponse,
  path: string,
  values: Record<string, unknown>,
  visibility: Record<string, PublicFieldVisibilityState>,
  includeHidden: boolean,
  context: RenderFormerieFormContext,
): string {
  const node = getPublicSchemaField(schema, path);
  return node ? renderSchemaNodeHtml(path, node, values, visibility, includeHidden, context) : "";
}

function renderStepsForFields(
  steps: PublicFormStep[],
  fields: FormerieRenderField[],
  steppedPaths: Set<string>,
  options: DescribeFormerieRenderModelOptions,
): FormerieRenderStep[] {
  return steps
    .map((step) => {
      const stepFields = fieldsForStepPaths(fields, step.fields, steppedPaths);

      return {
        fields: stepFields,
        id: step.id,
        label: step.label ?? labelFromPath(step.id),
        validateOn: step.validateOn,
      };
    })
    .filter((step) => options.includeEmptySteps === true || step.fields.length > 0);
}

function fieldsForStepPaths(
  fields: FormerieRenderField[],
  paths: string[],
  steppedPaths: Set<string>,
): FormerieRenderField[] {
  const emittedPaths = new Set<string>();
  const stepFields: FormerieRenderField[] = [];

  for (const path of paths) {
    for (const field of fields) {
      if (!fieldBelongsToStepPath(field, path) || emittedPaths.has(field.path)) {
        continue;
      }

      stepFields.push(field);
      emittedPaths.add(field.path);
      steppedPaths.add(field.path);
    }
  }

  return stepFields;
}

function fieldBelongsToStepPath(field: FormerieRenderField, path: string): boolean {
  return field.path === path || field.path.startsWith(`${path}.`);
}

function renderFieldHtml(field: FormerieRenderField, context: RenderFormerieFormContext): string {
  if (field.control === "checkbox") {
    return renderBooleanFieldHtml(field, context);
  }

  if (field.control === "checkbox-group") {
    return renderCheckboxGroupHtml(field, context);
  }

  const description = field.description
    ? renderFieldDescriptionHtml(field.description, "span", context)
    : "";
  const control = renderControlHtml(field);

  return `<label${fieldContainerAttributes(field, context)} for="${escapeAttribute(field.id)}">` +
    `<span>${escapeHtml(field.label)}</span>${description}${control}${renderFieldError(field.path, context)}` +
    `</label>`;
}

function renderControlHtml(field: FormerieRenderField): string {
  const common = commonControlAttributes(field);

  if (field.control === "textarea" || field.control === "array") {
    return `<textarea${common}${placeholderAttribute(field)}>${escapeHtml(textFieldValue(field.value))}</textarea>`;
  }

  if (field.control === "select") {
    const selectedValue = textFieldValue(field.value);
    const emptyOption = field.required ? "" : `<option value=""></option>`;
    const options = (field.options ?? [])
      .map((option) => {
        const selected = option === selectedValue ? " selected" : "";
        return `<option value="${escapeAttribute(option)}"${selected}>${escapeHtml(option)}</option>`;
      })
      .join("");
    return `<select${common}>${emptyOption}${options}</select>`;
  }

  return `<input${common} type="${escapeAttribute(field.inputType ?? "text")}"${placeholderAttribute(field)} value="${escapeAttribute(textFieldValue(field.value))}">`;
}

function renderBooleanFieldHtml(field: FormerieRenderField, context: RenderFormerieFormContext): string {
  const checked = field.value === true ? " checked" : "";
  const description = field.description
    ? renderFieldDescriptionHtml(field.description, "span", context)
    : "";

  return `<label${fieldContainerAttributes(field, context, context.classes.checkboxField)} for="${escapeAttribute(field.id)}">` +
    `<input${commonControlAttributes(field)} type="checkbox" value="true"${checked}>` +
    `<span>${escapeHtml(field.label)}</span>${description}${renderFieldError(field.path, context)}` +
    `</label>`;
}

function renderCheckboxGroupHtml(field: FormerieRenderField, context: RenderFormerieFormContext): string {
  const selected = new Set(Array.isArray(field.value) ? field.value.map(String) : []);
  const description = field.description
    ? renderFieldDescriptionHtml(field.description, "p", context)
    : "";
  const options = (field.options ?? [])
    .map((option, index) => {
      const optionId = `${field.id}-${index}`;
      const checked = selected.has(option) ? " checked" : "";
      const disabled = field.visibility === "hidden" ? " disabled" : "";
      return `<label for="${escapeAttribute(optionId)}">` +
        `<input id="${escapeAttribute(optionId)}" name="${escapeAttribute(field.name)}" data-formerie-path="${escapeAttribute(field.path)}" data-formerie-type="checkbox" type="checkbox" value="${escapeAttribute(option)}"${checked}${disabled}>` +
        `<span>${escapeHtml(option)}</span>` +
        `</label>`;
    })
    .join("");

  return `<fieldset${fieldContainerAttributes(field, context)}>` +
    `<legend>${escapeHtml(field.label)}</legend>${description}${options}${renderFieldError(field.path, context)}` +
    `</fieldset>`;
}

function fieldContainerAttributes(
  field: Pick<FormerieRenderField, "path" | "visibility">,
  context: RenderFormerieFormContext,
  className?: string,
): string {
  return serializeAttributes({
    "class": joinClassNames(context.classes.field, className),
    "data-formerie-field": field.path,
    "data-formerie-visibility": field.visibility,
    hidden: field.visibility === "hidden",
  });
}

function commonControlAttributes(field: FormerieRenderField): string {
  return serializeAttributes({
    "data-formerie-path": field.path,
    "data-formerie-type": field.node.type,
    disabled: field.visibility === "hidden",
    id: field.id,
    inputmode: field.inputMode,
    max: field.max,
    maxlength: field.maxLength,
    min: field.min,
    minlength: field.minLength,
    name: field.name,
    required: field.required,
    step: field.step,
  });
}

function placeholderAttribute(field: FormerieRenderField): string {
  return field.placeholder ? ` placeholder="${escapeAttribute(field.placeholder)}"` : "";
}

function renderFieldError(path: string, context: RenderFormerieFormContext): string {
  return `<span${serializeAttributes({
    "class": context.classes.fieldError,
    "data-formerie-error-for": path,
    "aria-live": "polite",
    hidden: true,
  })}></span>`;
}

function controlForScalarField(field: PublicScalarField): FormerieRenderControl {
  if (field.type === "textarea") {
    return "textarea";
  }

  if (field.type === "select") {
    return "select";
  }

  if (field.type === "boolean" || field.type === "checkbox") {
    return field.allowedValues && field.allowedValues.length > 0 ? "checkbox-group" : "checkbox";
  }

  return "input";
}

function inputTypeForScalarField(field: PublicScalarField): string | undefined {
  if (field.type === "email" || field.type === "url" || field.type === "number" || field.type === "date") {
    return field.type;
  }

  if (field.type === "string" && field.timeFormat) {
    return "time";
  }

  if (field.type === "phone") {
    return "tel";
  }

  return field.type === "string" ? "text" : undefined;
}

function inputModeForScalarField(field: PublicScalarField): string | undefined {
  if (field.type === "number") {
    return field.integer === true ? "numeric" : "decimal";
  }

  if (field.type === "phone") {
    return "tel";
  }

  return undefined;
}

function controlConstraintsForScalarField(
  field: PublicScalarField,
): Pick<FormerieRenderField, "max" | "maxLength" | "min" | "minLength" | "step"> {
  if (field.type === "number") {
    return {
      max: field.max,
      min: field.min,
      step: field.integer === true ? "1" : decimalStep(field.maxDecimalPlaces),
    };
  }

  if (field.type === "date") {
    return {
      max: field.dateMax,
      min: field.dateMin,
    };
  }

  if (field.type === "string" && field.timeFormat) {
    return {
      step: field.timeFormat === "HH:mm:ss" ? "1" : "60",
    };
  }

  if (field.type === "string" || field.type === "email" || field.type === "phone" || field.type === "textarea" || field.type === "url") {
    const exactLength = field.exactLength;

    return {
      maxLength: exactLength ?? field.max,
      minLength: exactLength ?? field.min,
    };
  }

  return {};
}

function decimalStep(maxDecimalPlaces: number | undefined): string {
  if (typeof maxDecimalPlaces === "number" && Number.isInteger(maxDecimalPlaces) && maxDecimalPlaces > 0) {
    return `0.${"0".repeat(Math.max(maxDecimalPlaces - 1, 0))}1`;
  }

  return "any";
}

function resolveMountElement(target: Element | string): Element {
  if (typeof target !== "string") {
    return target;
  }

  if (typeof document === "undefined") {
    throw new Error("mountFormerieForm requires a DOM document when target is a selector.");
  }

  const element = document.querySelector(target);
  if (!element) {
    throw new Error(`Formerie mount target was not found: ${target}`);
  }

  return element;
}

function resolveHostedFormElement(
  root: ParentNode | undefined,
  form: HTMLFormElement | string | undefined,
): HTMLFormElement {
  if (form && typeof form !== "string") {
    return form;
  }

  const selector = form ?? DEFAULT_HOSTED_FORM_SELECTOR;
  const queryRoot = root ?? currentDocument("bindFormerieHostedForm");
  const element = queryRoot.querySelector<HTMLFormElement>(selector);
  if (!element) {
    throw new Error(`Formerie hosted form was not found: ${selector}`);
  }

  return element;
}

function resolveHostedSchemaElement(
  root: ParentNode | undefined,
  schemaElement: HTMLScriptElement | string | undefined,
): HTMLScriptElement {
  if (schemaElement && typeof schemaElement !== "string") {
    return schemaElement;
  }

  const selector = schemaElement ?? DEFAULT_HOSTED_SCHEMA_SELECTOR;
  const queryRoot = root ?? currentDocument("readFormerieHostedSchema");
  const element = queryRoot.querySelector<HTMLScriptElement>(selector);
  if (!element) {
    throw new Error(`Formerie hosted schema script was not found: ${selector}`);
  }

  return element;
}

function currentDocument(caller: string): Document {
  if (typeof document === "undefined") {
    throw new Error(`${caller} requires a DOM document unless a root or element is provided.`);
  }

  return document;
}

async function resolveMaybeProvider<T>(
  value: T | (() => T | Promise<T>) | undefined,
): Promise<T | undefined> {
  return typeof value === "function"
    ? await (value as () => T | Promise<T>)()
    : value;
}

function setStatus(form: HTMLFormElement, message: string, state?: "error" | "pending" | "success"): void {
  const status = form.querySelector<HTMLElement>("[data-formerie-status]");
  if (status) {
    status.textContent = message;
    if (state) {
      status.setAttribute("data-state", state);
    } else {
      status.removeAttribute("data-state");
    }
  }
}

function setSubmitDisabled(form: HTMLFormElement, disabled: boolean): void {
  for (const button of Array.from(form.querySelectorAll<HTMLButtonElement>("[data-formerie-submit]"))) {
    button.disabled = disabled;
  }
}

function focusFirstInvalidField(form: HTMLFormElement): void {
  const field = form.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (field && typeof field.focus === "function") {
    field.focus();
  }
}

function resolvePublicFieldValue(
  values: Record<string, unknown>,
  path: string,
  fallback: unknown,
): unknown {
  const value = resolveFormerieFieldValue(values, path);
  return value === undefined ? fallback : value;
}

function schemaTitle(schema: PublicSchemaInput): string {
  if (isPublicFormSchema(schema)) {
    return schema.metadata?.label ??
      schema.settings?.branding?.brandName ??
      labelFromPath(schema.formHandle);
  }

  return "Form";
}

function schemaDescription(schema: PublicSchemaInput): string | undefined {
  return isPublicFormSchema(schema) ? schema.metadata?.description : undefined;
}

function schemaSubmitLabel(schema: PublicSchemaInput): string | undefined {
  return isPublicFormSchema(schema) ? schema.settings?.behavior?.submitButtonLabel : undefined;
}

function schemaSuccessMessage(schema: PublicFormSchemaResponse): string {
  return schema.settings?.behavior?.successMessage ?? "Thank you. Your response was received.";
}

function isPublicFormSchema(schema: PublicSchemaInput): schema is PublicFormSchemaResponse {
  return (schema as PublicFormSchemaResponse).ok === true;
}

function isPublicFormSchemaResponse(value: unknown): value is PublicFormSchemaResponse {
  return isRecord(value) &&
    value.ok === true &&
    typeof value.enabled === "boolean" &&
    typeof value.formHandle === "string" &&
    isRecord(value.fields) &&
    typeof value.schemaId === "string" &&
    typeof value.workspaceHandle === "string";
}

function schemaFields(schema: PublicSchemaInput): Record<string, PublicSchemaNode> {
  return isPublicFormSchema(schema) ? schema.fields : schema;
}

function fieldId(path: string): string {
  return `formerie-${path.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "field"}`;
}

function joinPath(prefix: string, key: string): string {
  return prefix ? `${prefix}.${key}` : key;
}

function labelFromPath(path: string): string {
  const segment = path.split(".").at(-1) ?? path;
  return segment
    .replace(/\[\]/g, "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function textFieldValue(value: unknown): string {
  if (value === undefined || value === null || Array.isArray(value)) {
    return "";
  }

  return String(value);
}

function serializeAttributes(attributes: Record<string, boolean | number | string | null | undefined>): string {
  const parts: string[] = [];

  for (const [name, value] of Object.entries(attributes)) {
    if (value === false || value === null || value === undefined) {
      continue;
    }

    const safeName = safeAttributeName(name);
    if (!safeName) {
      continue;
    }

    if (value === true || value === "") {
      parts.push(safeName);
      continue;
    }

    parts.push(`${safeName}="${escapeAttribute(String(value))}"`);
  }

  return parts.length > 0 ? ` ${parts.join(" ")}` : "";
}

function safeAttributeName(name: string): string | null {
  return /^[A-Za-z_:][A-Za-z0-9_:.-]*$/.test(name) ? name : null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value)
    .replace(/"/g, "&quot;");
}
